import React, { useEffect, useState } from "react";
import {
  Button,
  Container,
  Row,
  Col,
  Form,
  Card,
  Modal,
  Spinner,
} from "react-bootstrap";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import productItemEanApi from "../../services/productItemEanApi";
import ProductItemSearchSelect from "../../components/forms/ProductItemSearchSelect";
import { CatalogHeader } from "../../components/catalog/CatalogUI";

const ProductItemEanList = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchEan, setSearchEan] = useState("");
  const [searchRef, setSearchRef] = useState("");
  const [searchName, setSearchName] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [totalRecords, setTotalRecords] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState({ search: "", searchEan: "", searchRef: "" });

  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [primaryLoadingId, setPrimaryLoadingId] = useState(null);

  const [formData, setFormData] = useState({
    productItemId: "",
    eanCode: "",
    isPrimary: false,
  });

  const loadData = async (customFilters = {}) => {
    try {
      setLoading(true);

      const resp = await productItemEanApi.list({
        limit: paginationModel.pageSize,
        page: paginationModel.page,
        ...appliedFilters,
        ...customFilters,
      });

      const result = resp?.data?.result || [];
      setTotalRecords(Number(resp?.data?.totalRecords?.[0]?.total_records || 0));

      setRows(
        result.map((r, i) => ({
          id: r.id,
          sno: paginationModel.page * paginationModel.pageSize + i + 1,
          productName: r.productItem?.product?.productName || "",
          refCode: r.itemRefCode || r.item_ref_code || r.productItem?.refCode || "",
          eanCode: r.eanCode || r.ean_code || "",
          isPrimaryValue: Number(r.isPrimary ?? r.is_primary ?? 0),
          isPrimary: Number(r.isPrimary ?? r.is_primary ?? 0) === 1 ? "Yes" : "No",
          statusValue: Number(r.status),
          status: Number(r.status) === 1 ? "Active" : "Inactive",
        }))
      );
    } catch (error) {
      toast.error("Failed to load EAN codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [paginationModel, appliedFilters]);

  const handleSearch = () => {
    setAppliedFilters({ search: searchName.trim(), searchEan: searchEan.trim(), searchRef: searchRef.trim() });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleReset = () => {
    setSearchEan("");
    setSearchRef("");
    setSearchName("");
    setAppliedFilters({ search: "", searchEan: "", searchRef: "" });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const openAddModal = () => {
    setFormData({
      productItemId: "",
      eanCode: "",
      isPrimary: false,
    });
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (saving) return;
    setShowAddModal(false);
  };

  const handleCreateSingle = async () => {
    const productItemId = Number(formData.productItemId);
    const eanCode = String(formData.eanCode || "").trim();

    if (!productItemId) {
      toast.error("Please select product item");
      return;
    }

    if (!eanCode) {
      toast.error("Please enter EAN code");
      return;
    }

    try {
      setSaving(true);

      const resp = await productItemEanApi.createSingle({
        productItemId,
        eanCode,
        isPrimary: formData.isPrimary ? 1 : 0,
      });

      if (resp?.success === false) {
        toast.error(resp?.message || "Failed to add EAN code");
        return;
      }

      toast.success(resp?.message || "EAN code added successfully");
      setShowAddModal(false);
      loadData();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          "Failed to add EAN code"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMakePrimary = async (row) => {
    try {
      setPrimaryLoadingId(row.id);

      const resp = await productItemEanApi.setPrimary(row.id);

      if (resp?.success === false) {
        toast.error(resp?.message || "Failed to update primary EAN");
        return;
      }

      toast.success(resp?.message || "Primary EAN updated successfully");
      loadData();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          "Failed to update primary EAN"
      );
    } finally {
      setPrimaryLoadingId(null);
    }
  };

  const columns = [
    { field: "sno", headerName: "S.No", width: 90 },
    { field: "productName", headerName: "Product", width: 280 },
    { field: "refCode", headerName: "Reference Code", width: 180 },
    { field: "eanCode", headerName: "EAN Code", width: 220 },
    { field: "isPrimary", headerName: "Primary", width: 120 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "actions",
      headerName: "Action",
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;

        if (row.statusValue !== 1) {
          return <span className="text-muted">-</span>;
        }

        if (row.isPrimaryValue === 1) {
          return <span className="badge bg-success">Primary</span>;
        }

        return (
          <Button
            size="sm"
            variant="outline-primary"
            disabled={primaryLoadingId === row.id}
            onClick={() => handleMakePrimary(row)}
          >
            {primaryLoadingId === row.id ? "Updating..." : "Make Primary"}
          </Button>
        );
      },
    },
  ];

  return (
    <Container fluid className="catalog-page">
      <ToastContainer position="top-right" autoClose={2500} />

      <CatalogHeader title="Item EAN Codes" description="Find barcodes by product name, EAN or reference code. Manage primary codes and bulk uploads." count={totalRecords}
        actions={<>
          <Button variant="success" onClick={openAddModal}>
            Add Single EAN
          </Button>

          <Button
            variant="primary"
            onClick={() => navigate("/admin/products/item-ean-bulk-upload")}
          >
            Bulk Upload
          </Button>
        </>} />

      <Card className="mb-3">
        <Card.Body>
          <Form onSubmit={(event) => { event.preventDefault(); handleSearch(); }}>
          <Form.Group className="mb-3" controlId="eanProductSearch">
            <Form.Label>Product name, EAN or reference code</Form.Label>
            <Form.Control value={searchName} onChange={(event) => setSearchName(event.target.value)}
              placeholder="Search the full catalogue…" />
          </Form.Group>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Search by EAN Code</Form.Label>
                <Form.Control
                  type="text"
                  value={searchEan}
                  onChange={(e) => setSearchEan(e.target.value)}
                  placeholder="Enter EAN code"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Search by Reference Code</Form.Label>
                <Form.Control
                  type="text"
                  value={searchRef}
                  onChange={(e) => setSearchRef(e.target.value)}
                  placeholder="Enter reference code"
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <div className="d-flex gap-2">
                <Button variant="success" type="submit">
                  Search
                </Button>
                <Button variant="secondary" onClick={handleReset}>
                  Reset
                </Button>
              </div>
            </Col>
          </Row>
          </Form>
        </Card.Body>
      </Card>

      <div style={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          paginationMode="server"
          rowCount={totalRecords}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
        />
      </div>

      <Modal show={showAddModal} onHide={closeAddModal} centered size="lg" dialogClassName="catalog-modal">
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Add Single EAN Code</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Product Item</Form.Label>
                <ProductItemSearchSelect
                  value={formData.productItemId}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      productItemId: value,
                    }))
                  }
                  placeholder="Search product item by name / ref code"
                  isDisabled={saving}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group>
                <Form.Label>EAN Code</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.eanCode}
                  disabled={saving}
                  placeholder="Enter EAN code"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eanCode: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Check
                type="checkbox"
                label="Set as Primary EAN"
                checked={formData.isPrimary}
                disabled={saving}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isPrimary: e.target.checked,
                  }))
                }
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeAddModal} disabled={saving}>
            Cancel
          </Button>

          <Button variant="success" onClick={handleCreateSingle} disabled={saving}>
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save EAN"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProductItemEanList;
